import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { NotificationsBasePath } from '@/hooks/notifications';
import type { PushConfigView } from '@shared/schemas/notification';

/*
 * "Notifications on this device" — the browser half of §11.4, living in the
 * tray so it is one control in all three portals.
 *
 * Push is per-browser-per-device, which is why this is not a row on the
 * preference screens: those switch notification *types* for the account; this
 * asks *this phone* to show them at all. The two compose — every push still
 * checks the per-type push preference on the server.
 *
 * Every state explains itself rather than hiding (design-system §7) — the
 * prototype's one praised capability message is its "blocked in your browser
 * settings" line, kept here word-for-word in spirit.
 */

type PushState = 'unsupported' | 'unavailable' | 'blocked' | 'off' | 'on';

/** Fixed for the lifetime of the page — a browser does not grow a Push API. */
const PUSH_SUPPORTED =
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

function base64UrlToUint8Array(value: string): Uint8Array {
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

async function currentRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  // Not `.ready` — that promise never settles when nothing is registered, and
  // in development the worker deliberately is not (see main.tsx).
  return navigator.serviceWorker.getRegistration();
}

interface PushStatus {
  state: PushState;
  publicKey: string | null;
}

async function resolvePushStatus(basePath: NotificationsBasePath): Promise<PushStatus> {
  if (!PUSH_SUPPORTED) return { state: 'unsupported', publicKey: null };

  const config = await api
    .get<PushConfigView>(`${basePath}/notifications/push/config`)
    .catch(() => null);
  if (!config?.publicKey) return { state: 'unavailable', publicKey: null };

  const registration = await currentRegistration();
  if (!registration) return { state: 'unavailable', publicKey: config.publicKey };

  if (Notification.permission === 'denied') return { state: 'blocked', publicKey: null };

  const subscription = await registration.pushManager.getSubscription();
  return { state: subscription ? 'on' : 'off', publicKey: config.publicKey };
}

export function PushDeviceToggle({ basePath }: { basePath: NotificationsBasePath }) {
  const queryClient = useQueryClient();
  const statusKey = ['push-status', basePath];

  const status = useQuery({
    queryKey: statusKey,
    queryFn: () => resolvePushStatus(basePath),
    /*
     * Browser permission and subscription state change entirely outside our
     * code — in browser settings, on another surface — so this overrides the
     * app's refetchOnMount: false: the toggle mounts only when the tray
     * opens, opening it is rare and deliberate, and a cached wrong answer
     * here (found by watching one) otherwise survives until a window-focus
     * refetch that a person reading the tray never triggers.
     */
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const enable = useMutation({
    mutationFn: async () => {
      const publicKey = status.data?.publicKey;
      if (!publicKey) return;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const registration = await currentRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8Array(publicKey).buffer as ArrayBuffer,
      });

      await api.put(`${basePath}/notifications/push/subscription`, subscription.toJSON());
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });

  const disable = useMutation({
    mutationFn: async () => {
      const registration = await currentRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription) return;

      // Server first: if the browser half fails after this, the worst case is
      // a dead subscription the 410 path cleans up — not a device that keeps
      // buzzing after the person said stop.
      await api.post(`${basePath}/notifications/push/unsubscribe`, {
        endpoint: subscription.endpoint,
      });
      await subscription.unsubscribe();
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });

  const state = status.data?.state;
  if (!state) return null;

  if (state === 'unsupported' || state === 'unavailable') {
    return (
      <p className="px-3 py-2 text-meta text-ink-500">
        {state === 'unsupported'
          ? 'This browser cannot show push notifications.'
          : 'Push notifications are not available here yet.'}
      </p>
    );
  }

  if (state === 'blocked') {
    return (
      <p className="px-3 py-2 text-meta text-ink-500">
        Push notifications are blocked in your browser settings for this site.
      </p>
    );
  }

  const busy = enable.isPending || disable.isPending;

  return (
    <div className="flex items-center justify-between gap-2 px-3 py-1.5">
      <span className="text-label text-ink-700">Push to this device</span>
      <Button
        variant={state === 'on' ? 'outline' : 'primary'}
        size="sm"
        disabled={busy}
        onClick={() => {
          (state === 'on' ? disable : enable).mutate();
        }}
      >
        {busy ? 'Working…' : state === 'on' ? 'Turn off' : 'Turn on'}
      </Button>
    </div>
  );
}
